
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@supabase/auth-helpers-react";

const BIBLE_VERSIONS = [
  { id: "KJV", name: "King James Version" },
  { id: "NASB", name: "New American Standard Bible" },
  { id: "9879dbb7cfe39e4d-01", name: "World English Bible" },
] as const;

export function BibleVersionSelect() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [currentVersion, setCurrentVersion] = useState("KJV");
  const session = useSession();

  useEffect(() => {
    if (session?.user) {
      fetchCurrentVersion();
    }
  }, [session?.user]);

  const fetchCurrentVersion = async () => {
    if (!session?.user) return;

    try {
      setIsLoading(true);
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('bible_version')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) throw error;

      // If no profile exists, we shouldn't create one here
      // This component should only update existing profiles
      if (!profile) {
        console.warn('No profile found for user');
        return;
      }

      if (profile.bible_version) {
        setCurrentVersion(profile.bible_version);
      }
    } catch (error) {
      console.error('Error fetching bible version:', error);
      toast({
        title: "Error",
        description: "Failed to load Bible version preference",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVersionChange = async (version: string) => {
    if (!session?.user) return;

    setIsLoading(true);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          bible_version: version,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id);

      if (updateError) throw updateError;

      setCurrentVersion(version);
      toast({
        title: "Bible Version Updated",
        description: `Changed to ${BIBLE_VERSIONS.find(v => v.id === version)?.name}`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Error changing Bible version:', error);
      toast({
        title: "Error",
        description: "Failed to update Bible version. Please try again.",
        duration: 3000,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Label className="text-app-text-primary">Bible Version</Label>
        <div className="h-10 bg-app-surface/50 animate-pulse rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-app-text-primary">Bible Version</Label>
      <Select onValueChange={handleVersionChange} value={currentVersion} disabled={isLoading}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select Bible version" />
        </SelectTrigger>
        <SelectContent>
          {BIBLE_VERSIONS.map((version) => (
            <SelectItem key={version.id} value={version.id}>
              {version.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-sm text-app-text-secondary">
        Choose your preferred Bible translation
      </p>
    </div>
  );
}
